import { authenticatedFetch } from "./http";
import type {
  ConfigurationProfileCode,
  ConfigurationProfilePreset,
  ConfigurationProfilePreview,
  ConfigurationProfileState
} from "./models";

export function loadConfigurationProfiles(): Promise<ConfigurationProfilePreset[]> {
  return authenticatedFetch<ConfigurationProfilePreset[]>(
    "/api/v1/configuration/profiles"
  );
}

export async function loadCurrentConfigurationProfile(): Promise<ConfigurationProfileState | null> {
  const state = await authenticatedFetch<ConfigurationProfileState | undefined>(
    "/api/v1/configuration/profiles/current"
  );
  return state ?? null;
}

export function previewConfigurationProfile(
  code: ConfigurationProfileCode
): Promise<ConfigurationProfilePreview> {
  return authenticatedFetch<ConfigurationProfilePreview>(
    `/api/v1/configuration/profiles/${code}/preview`
  );
}

export function applyConfigurationProfile(
  code: ConfigurationProfileCode,
  profileVersion: number
): Promise<ConfigurationProfileState> {
  return authenticatedFetch<ConfigurationProfileState>(
    `/api/v1/configuration/profiles/${code}/apply`,
    { method: "POST", body: JSON.stringify({ profileVersion }) }
  );
}

export interface BusinessPartnerMember {
  id: string;
  displayName: string;
  email: string;
  role: string;
}

export interface BusinessPartner {
  id: string;
  code: string;
  displayName: string;
  kind: "CLIENT" | "SUPPLIER" | "INTERNAL_TEAM" | "OTHER";
  status: "ACTIVE" | "INACTIVE";
  version: number;
  clientId: string | null;
  supplierId: string | null;
  members: BusinessPartnerMember[];
}

export interface BusinessPartnerUserOption {
  id: string;
  displayName: string;
  email: string;
}

export interface BusinessPartnerConfiguration {
  businessPartners: BusinessPartner[];
  users: BusinessPartnerUserOption[];
}

export const loadBusinessPartners = () =>
  authenticatedFetch<BusinessPartnerConfiguration>(
    "/api/v1/configuration/business-partners"
  );

export const createBusinessPartner = (request: {
  code: string; displayName: string; kind: "INTERNAL_TEAM" | "OTHER";
  memberIds: string[];
}) => authenticatedFetch<BusinessPartner>(
  "/api/v1/configuration/business-partners",
  { method: "POST", body: JSON.stringify(request) }
);

export const updateBusinessPartner = (partner: BusinessPartner) =>
  authenticatedFetch<BusinessPartner>(
    `/api/v1/configuration/business-partners/${partner.id}`,
    { method: "PUT", body: JSON.stringify({
      code: partner.code, displayName: partner.displayName,
      status: partner.status, version: partner.version,
      memberIds: partner.members.map((member) => member.id)
    }) }
  );
